import React, { useEffect, useState } from 'react';

import { Anchor, Flex, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconExternalLink, IconWorld } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { DroxyBoxedIcon } from 'components/Droxy/DroxyBoxedIcon';
import { ErrorAlert } from 'components/ErrorAlert';
import { ExtractedContentLayout } from 'components/ExtractedContent/ExtractedContentLayout';
import { ExtractedContentPreview } from 'components/ExtractedContent/ExtractedContentPreview/ExtractedContentPreview';
import { NavigationItem } from 'components/ExtractedContent/ExtractedItemsNavigation/NavigationItem';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { ResourceViewerWrapper } from 'components/ResourceViewerWrapper/ResourceViewerWrapper';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { ResourceService } from 'services/resource-service';
import { WebsiteService } from 'services/website-service';
import { RESOURCE_QUERY_KEY, WEBSITE_TEXT_DATA_QUERY_KEY } from 'shared/constants/query-keys';
import { UrlTextData } from 'shared/interfaces/Website';

const urlTextDataToReadableMarkdown = (urlTextData?: UrlTextData): string => {
  let result = '';

  if (!urlTextData) return result;

  // Need to do this for legacy website resources
  const sections = urlTextData.sections || [];
  if (sections.length === 0) return urlTextData.text;

  for (const section of sections) {
    result += `## ${section.title}\n`;
    result += `### ${section.summary}\n`;
    result += `${section.content}\n\n---\n\n`;
  }

  return result;
};

export function WebsiteViewerPage() {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const { resourceId = '' } = useParams();
  const [currentUrl, setCurrentUrl] = useState<string | undefined>(undefined);

  const resourceQuery = useQuery({
    queryKey: [RESOURCE_QUERY_KEY, { resourceId: resourceId }],
    queryFn: () => ResourceService.getResourceInfo(resourceId),
    refetchOnWindowFocus: false
  });

  const websiteTextDataQuery = useQuery({
    queryKey: [WEBSITE_TEXT_DATA_QUERY_KEY, { resourceId: resourceId }],
    queryFn: () => WebsiteService.getWebsiteTextData(resourceId),
    enabled: !!resourceId,
    retry: false
  });

  useEffect(() => {
    if (resourceQuery.data?.website) {
      setCurrentUrl(resourceQuery.data.website.url);
    }
  }, [resourceQuery.data]);

  const searchForm = useForm({
    initialValues: { search: '' }
  });

  const handleUrlItemClick = (url: string) => {
    setCurrentUrl(url);
  };

  if (websiteTextDataQuery.isLoading || resourceQuery.isLoading || !websiteTextDataQuery.data)
    return <FullScreenLoader />;
  if (websiteTextDataQuery.isError || resourceQuery.isError) return <ErrorAlert />;

  const filteredUrls = websiteTextDataQuery.data.urlsTextData.filter((urlData) => {
    const searchTerm = searchForm.values.search.toLowerCase();
    if (!searchTerm) return true;

    const urlMatch = urlData.url.toLowerCase().includes(searchTerm);
    const titleMatch = urlData.title && urlData.title.toLowerCase().includes(searchTerm);
    return urlMatch || titleMatch;
  });

  // Move base URL to the front if it exists in the filtered results
  const baseUrl = resourceQuery.data?.website?.url;
  const baseUrlIndex = filteredUrls.findIndex((urlData) => urlData.url === baseUrl);
  if (baseUrlIndex > 0) {
    const [baseUrlData] = filteredUrls.splice(baseUrlIndex, 1);
    filteredUrls.unshift(baseUrlData);
  }

  const currentUrlData = websiteTextDataQuery.data.urlsTextData.find((urlData) => urlData.url === currentUrl);

  return (
    <ResourceViewerWrapper
      resourceId={resourceId}
      fileName={resourceQuery.data?.fileName || currentUrlData?.title}
      backgroundColor={theme.colors.gray[0]}>
      <ExtractedContentLayout
        navigationTitle={t('WEBSITE_VIEWER.NAVIGATION.TITLE')}
        navigationTooltip={t('WEBSITE_VIEWER.NAVIGATION.TOOLTIP')}
        searchForm={searchForm}
        navigationItems={
          <>
            {filteredUrls.map((urlData) => (
              <NavigationItem
                key={urlData.url}
                id={urlData.url}
                title={urlData.title || t('WEBSITE_VIEWER.NAVIGATION.URL_ITEM_FALLBACK')}
                description={urlData.url}
                isActive={urlData.url === currentUrl}
                onClick={handleUrlItemClick}
                icon={<DroxyBoxedIcon icon={<IconWorld color={theme.colors.gray[5]} />} hasBorder />}
              />
            ))}
          </>
        }
        mainContent={
          <ExtractedContentPreview
            title={currentUrlData?.title || t('WEBSITE_VIEWER.MAIN_CONTENT.URL_FALLBACK')}
            content={urlTextDataToReadableMarkdown(currentUrlData)}
            contentKey={currentUrl || ''}
            subtitleComponent={
              <Flex gap={4} align="center" style={{ maxWidth: 700 }}>
                <Anchor
                  style={{ color: theme.colors.gray[6] }}
                  href={currentUrl}
                  truncate
                  target="_blank"
                  rel="noopener noreferrer">
                  {currentUrl}
                </Anchor>
                <IconExternalLink style={{ width: 16, height: 16, color: theme.colors.gray[6], marginBottom: 2 }} />
              </Flex>
            }
          />
        }
      />
    </ResourceViewerWrapper>
  );
}
